import { Group, Stack, Text, Textarea, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { showNotification, updateNotification } from "@mantine/notifications";
import {
  getNotificationByResultType,
  InventoryItemLocation,
  loadingInfoNotification,
  Result,
} from "beitary-shared";
import { BBox, SaveButton } from "components";
import { MoveBackButton } from "components/MoveBackButton";
import { useDBServices } from "hooks/useDBService/useDBService";
import { useSubmitState } from "hooks/useSubmitState";
import { useTranslation } from "react-i18next";
import { rules, WriteLocationFormValues } from "./WriteLocationForm.rules";

export interface WriteLocationFormProps {
  location?: InventoryItemLocation;
}

export const WriteLocationForm = ({ location }: WriteLocationFormProps) => {
  const [submitState, setSubmitState] = useSubmitState();
  const { t } = useTranslation();
  const { editInventoryItemLocation, addInventoryItemLocation } =
    useDBServices().inventoryDBService.inventoryItemLocations;

  const form = useForm<WriteLocationFormValues>({
    initialValues: location ? { ...location } : undefined,
    validate: rules,
  });

  if (!form.values.status) form.setFieldValue("status", "ACTIVE");

  const submit = async (values: WriteLocationFormValues) => {
    const notificationId = "submit-inventory-item-location";
    showNotification(
      loadingInfoNotification({
        id: notificationId,
        message: t("Waiting for server response"),
        title: t(location ? "UPDATE_LOCATION" : "ADD_LOCATION"),
      })
    );
    setSubmitState("pending-response");
    let result: Result<InventoryItemLocation | null>;
    if (location) {
      result = await editInventoryItemLocation(location.id, values);
    } else {
      result = await addInventoryItemLocation(values);
    }
    if (result.type === "success") {
      setSubmitState("success");
    } else {
      setSubmitState("error");
    }
    updateNotification({
      ...getNotificationByResultType(result.type)({
        message: t(result.message),
      }),
      id: notificationId,
    });
  };

  return (
    <form onSubmit={form.onSubmit(submit, (errors) => console.log(errors))}>
      <Stack>
        <Group>
          <MoveBackButton />
        </Group>
        <BBox
          header={
            location ? (
              <Text weight={500}>{t("EDIT_LOCATION")}</Text>
            ) : (
              <Text weight={500}>{t("NEW_LOCATION")}</Text>
            )
          }
        >
          <Stack p="xl">
            <TextInput
              required
              placeholder={t("ENTER_NAME")}
              label={t("NAME")}
              {...form.getInputProps("name")}
            />
            <Textarea
              placeholder={t("ENTER_NOTES")}
              label={t("NOTES")}
              {...form.getInputProps("notes")}
            />
          </Stack>
        </BBox>
        <Group position="right">
          <SaveButton state={submitState} canSave={form.isValid()} />
        </Group>
      </Stack>
    </form>
  );
};
